// appTheme.js
// ONE brand-matched MUI theme for the whole app, built from the app's LIVE
// Bootstrap tokens (`--bs-*` CSS custom properties) so every MUI surface —
// buttons, inputs, selects, dialogs — matches the brand AND flips correctly in
// light/dark mode, with no hardcoded hexes to drift.
//
// This is the single source of truth: AppThemeProvider wraps <App/> with it so
// MUI renders on-brand app-wide, and AppModal re-uses the same builder for its
// scoped theme (see AppModal/appModalTheme.js which re-exports buildAppTheme).

import { createTheme } from "@mui/material/styles";

/* Read a CSS custom property off :root, trimmed, with a fallback. */
function cssVar(name, fallback) {
  if (typeof window === "undefined" || !document?.documentElement) return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name);
  return v && v.trim() ? v.trim() : fallback;
}

/* Parse "#rrggbb", "#rgb" or "r,g,b" / "rgb(r,g,b)" → {r,g,b}. */
function toRgb(color) {
  if (!color) return null;
  const c = color.trim();
  if (c.startsWith("#")) {
    let hex = c.slice(1);
    if (hex.length === 3) hex = hex.split("").map((x) => x + x).join("");
    if (hex.length < 6) return null;
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }
  const nums = c.replace(/rgba?\(|\)/g, "").split(",").map((x) => parseFloat(x));
  if (nums.length >= 3 && nums.every((n) => !Number.isNaN(n))) {
    return { r: nums[0], g: nums[1], b: nums[2] };
  }
  return null;
}

/* Perceived luminance 0..1 — used to decide light vs dark palette mode. */
function luminance(color) {
  const rgb = toRgb(color);
  if (!rgb) return 1; // assume light if unknown
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
}

/* Bootstrap exposes `--bs-primary` (hex) in 5.3; some builds only ship the
   `-rgb` triplet. Prefer the hex, fall back to composing from the triplet. */
function brand(name, fallback) {
  const hex = cssVar(`--bs-${name}`, "");
  if (hex) return hex;
  const rgb = cssVar(`--bs-${name}-rgb`, "");
  return rgb ? `rgb(${rgb})` : fallback;
}

/**
 * Build the app-wide MUI theme from the current Bootstrap tokens.
 * Call this on mount and whenever the brand/theme mode changes so it captures
 * the active theme (incl. dark).
 */
export function buildAppTheme() {
  const bodyBg = cssVar("--bs-body-bg", "#ffffff");
  const bodyColor = cssVar("--bs-body-color", "#212529");
  const borderColor = cssVar("--bs-border-color", "#e9ebec");
  const secondaryText = cssVar("--bs-secondary-color", "rgba(33,37,41,0.6)");
  // Secondary/tertiary surface — used for input backgrounds so fields read as
  // part of the page, not floating white boxes.
  const tertiaryBg = cssVar("--bs-tertiary-bg", bodyBg);
  // Match the page's font so MUI doesn't read as a foreign component.
  const fontFamily =
    cssVar("--bs-body-font-family", "") ||
    (typeof document !== "undefined" && document.body
      ? getComputedStyle(document.body).fontFamily
      : "inherit");

  const mode = luminance(bodyBg) < 0.5 ? "dark" : "light";
  const primary = brand("primary", "#405189");

  return createTheme({
    palette: {
      mode,
      primary: { main: primary },
      secondary: { main: brand("secondary", "#865ce2") },
      success: { main: brand("success", "#0ab39c") },
      info: { main: brand("info", "#299cdb") },
      warning: { main: brand("warning", "#f7b84b") },
      error: { main: brand("danger", "#f06548") },
      background: { paper: bodyBg, default: bodyBg },
      text: { primary: bodyColor, secondary: secondaryText },
      divider: borderColor,
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily,
      button: { textTransform: "none", fontWeight: 600 },
      h6: { fontWeight: 700 },
    },
    components: {
      MuiDialog: {
        defaultProps: { fullWidth: true },
        styleOverrides: {
          paper: {
            borderRadius: 16,
            border: `1px solid ${borderColor}`,
            backgroundImage: "none",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 8, paddingInline: 16 } },
      },
      // Compact, brand-consistent inputs everywhere.
      MuiTextField: {
        defaultProps: { size: "small", variant: "outlined" },
      },
      MuiSelect: { defaultProps: { size: "small" } },
      MuiAutocomplete: { defaultProps: { size: "small" } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8, backgroundColor: tertiaryBg },
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { fontWeight: 500 } },
      },
    },
  });
}
